
import Icon from '@/components/ui/AppIcon';

interface Advantage {
  icon: string;
  title: string;
  description: string;
}

interface Partnership {
  name: string;
  logo: string;
  alt: string;
  description: string;
}

interface Certification {
  name: string;
  issuer: string;
  verificationUrl: string;
  icon: string;
}

const CompetitiveAdvantages = () => {
  const advantages: Advantage[] = [
  {
    icon: 'HandshakeIcon',
    title: 'Direct Manufacturer Partnerships',
    description: 'Exclusive relationships with Schneider Electric, Toshiba, and Fuji Electric ensure genuine products, MEP materials, and competitive pricing'
  },
  {
    icon: 'ClockIcon',
    title: 'Rapid Deployment',
    description: 'Average project delivery 20% faster than industry standard through efficient processes'
  },
  {
    icon: 'CurrencyDollarIcon',
    title: 'Cost Optimization',
    description: 'Direct sourcing and efficient operations deliver 15-25% cost savings without compromising quality'
  },
  {
    icon: 'UserGroupIcon',
    title: 'Expert Engineering Team',
    description: '200+ certified engineers with international training and local market expertise'
  },
  {
    icon: 'WrenchScrewdriverIcon',
    title: 'Comprehensive After-Sales',
    description: '24/7 support, preventive maintenance programs, and genuine spare parts availability'
  }];


  const partnerships: Partnership[] = [
  {
    name: 'Schneider Electric',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8e56167-1766569701959.png",
    alt: 'Schneider Electric logo on modern industrial electrical equipment panel with green and white branding',
    description: 'Authorized distributor for power distribution and automation solutions'
  },
  {
    name: 'Toshiba',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4a991c0-1765061351919.png",
    alt: 'Toshiba corporate branding on industrial electrical transformer equipment with red logo',
    description: 'Partner for transformers and heavy electrical equipment'
  },
  {
    name: 'Fuji Electric',
    logo: "https://images.unsplash.com/photo-1733192809243-f089cefcd421",
    alt: 'Fuji Electric industrial control panel with blue branding and digital display systems',
    description: 'Collaboration on industrial drives and control systems'
  }];


  const certifications: Certification[] = [
  {
    name: 'EPRA Certified',
    issuer: 'Energy and Petroleum Regulatory Authority',
    verificationUrl: '#',
    icon: 'BoltIcon'
  },
  {
    name: 'NCA Approved',
    issuer: 'National Construction Authority',
    verificationUrl: '#',
    icon: 'BuildingOfficeIcon'
  },
  {
    name: 'ICT Certified',
    issuer: 'Information and Communications Technology',
    verificationUrl: '#',
    icon: 'SignalIcon'
  },
  {
    name: 'ISO 9001:2015',
    issuer: 'International Organization for Standardization',
    verificationUrl: '#',
    icon: 'CheckBadgeIcon'
  }];


  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
              Why Choose New Capital
            </h2>
            <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
              Unmatched expertise, partnerships, and commitment to excellence set us apart
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {advantages.map((advantage) =>
            <div
              key={advantage.title}
              className="bg-surface rounded-lg p-6 hover:shadow-card transition-all duration-250 transform hover:-translate-y-1">

                <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mb-4">
                  <Icon name={advantage.icon as any} size={32} variant="solid" className="text-white" />
                </div>
                <h3 className="font-headline text-xl font-semibold text-primary mb-2">{advantage.title}</h3>
                <p className="font-body text-muted-foreground">{advantage.description}</p>
              </div>
            )}
          </div>

          <div>
            <h3 className="font-headline text-3xl font-bold text-primary mb-8 text-center">
              Certifications & Compliance
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {certifications.map((cert) =>
              <a
                key={cert.name}
                href={cert.verificationUrl}
                className="bg-surface rounded-lg p-6 text-center hover:shadow-card transition-all duration-250 transform hover:-translate-y-1 group">

                  <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:bg-accent transition-colors duration-250">
                    <Icon name={cert.icon as any} size={32} variant="solid" className="text-white" />
                  </div>
                  <h4 className="font-headline text-lg font-bold text-primary mb-1">{cert.name}</h4>
                  <p className="font-body text-xs text-muted-foreground mb-3">{cert.issuer}</p>
                  <div className="flex items-center justify-center gap-1 text-accent text-sm font-semibold">
                    <span>Verify</span>
                    <Icon name="ArrowTopRightOnSquareIcon" size={16} variant="outline" />
                  </div>
                </a>
              )}
            </div>
          </div>

          <div className="mt-16 bg-success/10 border-2 border-success rounded-lg p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Icon name="ShieldCheckIcon" size={48} variant="solid" className="text-success" />
              <h3 className="font-headline text-3xl font-bold text-primary">Zero-Incident Safety Record</h3>
            </div>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
              Over 5 million man-hours worked without a lost-time incident, demonstrating our unwavering commitment to safety excellence
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="bg-card rounded-lg p-4">
                <div className="font-headline text-4xl font-bold text-success mb-1">100%</div>
                <div className="font-body text-sm text-muted-foreground">Safety Compliance</div>
              </div>
              <div className="bg-card rounded-lg p-4">
                <div className="font-headline text-4xl font-bold text-success mb-1">5M+</div>
                <div className="font-body text-sm text-muted-foreground">Safe Man-Hours</div>
              </div>
              <div className="bg-card rounded-lg p-4">
                <div className="font-headline text-4xl font-bold text-success mb-1">0</div>
                <div className="font-body text-sm text-muted-foreground">Lost-Time Incidents</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CompetitiveAdvantages;